import LegalPage from "@/components/LegalPage";

const TermsPage = () => (
  <LegalPage
    title="Terms of Service"
    updated="June 18, 2026"
    intro={
      <p>
        These Terms of Service ("Terms") govern your access to and use of Vecurate, the website at
        vecurate.net, and any related services (collectively, the "Service"). By creating an account,
        submitting a review, or otherwise using the Service, you agree to these Terms. If you do not agree,
        do not use the Service.
      </p>
    }
  >
    <h2>1. About Vecurate</h2>
    <p>
      Vecurate is a community platform where students, trainees, and professionals can search, submit, and
      read anonymous reviews of internships, clinical rotations, preceptors, employers, residency programs,
      and other educational opportunities. The Service is intended to help users make informed decisions
      based on the firsthand experiences of others.
    </p>

    <h2>2. Eligibility and Accounts</h2>
    <p>
      You must be at least 16 years old to use the Service. By using the Service, you represent that the
      information you provide is accurate and that you have the legal capacity to enter into these Terms.
    </p>
    <p>
      You are responsible for safeguarding your account credentials and for all activity that occurs under
      your account. Notify Vecurate promptly at <a href="mailto:support@vecurate.net">support@vecurate.net</a>{" "}
      if you suspect unauthorized use of your account.
    </p>

    <h2>3. Nature of Reviews</h2>
    <ul>
      <li>
        <strong>Opinions only.</strong> Reviews and ratings posted on Vecurate represent the personal
        opinions and individual experiences of the users who submit them. They do not represent the views
        of Vecurate.
      </li>
      <li>
        <strong>No verification of every review.</strong> Vecurate does not independently verify the
        accuracy, completeness, or truthfulness of every review. We rely on community standards, automated
        signals, and user reporting to maintain quality, but we cannot guarantee that any specific review
        is accurate.
      </li>
      <li>
        <strong>User responsibility.</strong> You are solely responsible for the content you submit,
        including reviews, comments, ratings, and any other material ("User Content"), and for any
        consequences of posting it.
      </li>
    </ul>

    <h2>4. Prohibited Conduct and Content</h2>
    <p>You agree not to use the Service to post or engage in:</p>
    <ul>
      <li>Harassment, threats, intimidation, or targeted abuse of any individual or group;</li>
      <li>Discrimination or hate speech based on race, ethnicity, national origin, religion, sex, gender identity, sexual orientation, disability, age, or any other protected characteristic;</li>
      <li>Doxxing or publishing private or identifying information about another person without consent (including home addresses, phone numbers, or personal email addresses);</li>
      <li>Impersonation of any person, organization, preceptor, employer, or Vecurate staff;</li>
      <li>Knowingly false, defamatory, fabricated, or misleading statements;</li>
      <li>Coordinated reputation attacks, vote manipulation, or fake review campaigns;</li>
      <li>Spam, advertising, solicitation, or commercial promotion unrelated to a genuine review;</li>
      <li>Protected health information (PHI), patient information, trade secrets, or confidential information you are not authorized to share;</li>
      <li>Material that infringes intellectual property rights or violates any applicable law;</li>
      <li>Malware, scraping, automated access, or attempts to disrupt or compromise the Service.</li>
    </ul>
    <p>
      For additional detail, see our <a href="/content-policy">Content Policy</a> and{" "}
      <a href="/community-guidelines">Community Guidelines</a>.
    </p>

    <h2>5. Moderation and Enforcement</h2>
    <p>
      Vecurate reserves the right, but is not obligated, to review, remove, edit, or restrict access to any
      User Content for any reason, including suspected violation of these Terms. We may also suspend,
      restrict, or terminate accounts that violate these Terms or that we reasonably believe pose a risk to
      the community.
    </p>
    <p>
      Where appropriate, we will notify affected users and provide an opportunity to appeal. Some
      enforcement actions (for example, those involving safety, legal risk, or coordinated abuse) may be
      taken without prior notice.
    </p>

    <h2>6. License to User Content</h2>
    <p>
      You retain ownership of the User Content you submit. By submitting User Content, you grant Vecurate a
      worldwide, non-exclusive, royalty-free, sublicensable license to host, store, reproduce, display,
      adapt, and distribute that content in connection with operating and promoting the Service. This
      license ends when you or Vecurate removes the content, except to the extent the content has been
      shared with others who have not removed it or where retention is required by law.
    </p>

    <h2>7. Intellectual Property</h2>
    <p>
      The Service, including its design, branding, logos, text, and software, is owned by Vecurate and is
      protected by intellectual property laws. Except for the limited rights expressly granted in these
      Terms, you receive no rights in or to the Service.
    </p>

    <h2>8. Copyright (DMCA)</h2>
    <p>
      Vecurate respects intellectual property rights and responds to valid notices of alleged copyright
      infringement. See our <a href="/dmca">DMCA / Copyright Policy</a> for instructions on submitting a
      takedown notice or counter-notification.
    </p>

    <h2>9. Disclaimers</h2>
    <p>
      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
      IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
      NON-INFRINGEMENT, AND ANY WARRANTY ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE. VECURATE DOES
      NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY REVIEW OR USER CONTENT
      IS ACCURATE, COMPLETE, OR RELIABLE.
    </p>
    <p>
      Vecurate is not a substitute for professional, legal, medical, employment, or academic advice. You
      should make your own evaluation before relying on any review on the Service.
    </p>

    <h2>10. Limitation of Liability and Disclaimer</h2>
    <p>
      THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. TO THE MAXIMUM EXTENT PERMITTED BY
      APPLICABLE LAW, VECURATE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
      MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, RELIABILITY,
      AND AVAILABILITY.
    </p>
    <p>
      VECURATE DOES NOT VERIFY, GUARANTEE, ENDORSE, OR WARRANT THE ACCURACY, COMPLETENESS, RELIABILITY,
      OR USEFULNESS OF ANY USER-GENERATED CONTENT, INCLUDING REVIEWS, RATINGS, COMMENTS, OPINIONS, OR
      OTHER INFORMATION POSTED BY USERS. ALL USER-GENERATED CONTENT REPRESENTS THE VIEWS OF ITS
      RESPECTIVE AUTHORS AND NOT VECURATE.
    </p>
    <p>
      USERS ASSUME FULL RESPONSIBILITY FOR ANY DECISIONS MADE OR ACTIONS TAKEN BASED ON CONTENT AVAILABLE
      THROUGH THE SERVICE. VECURATE SHALL NOT BE RESPONSIBLE FOR ANY EMPLOYMENT, EDUCATIONAL,
      PROFESSIONAL, FINANCIAL, LICENSING, CAREER, OR PERSONAL DECISIONS MADE IN RELIANCE ON INFORMATION
      FOUND ON THE PLATFORM.
    </p>
    <p>
      TO THE FULLEST EXTENT PERMITTED BY LAW, VECURATE, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
      CONTRACTORS, AGENTS, AND REPRESENTATIVES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
      CONSEQUENTIAL, SPECIAL, EXEMPLARY, PUNITIVE, OR ENHANCED DAMAGES, INCLUDING LOSS OF PROFITS, LOSS
      OF REPUTATION, LOSS OF BUSINESS OPPORTUNITIES, LOSS OF DATA, LOSS OF EDUCATIONAL OPPORTUNITIES,
      LOSS OF EMPLOYMENT OPPORTUNITIES, OR BUSINESS INTERRUPTION ARISING OUT OF OR RELATING TO THE USE
      OF, OR INABILITY TO USE, THE SERVICE.
    </p>
    <p>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, VECURATE'S TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING
      OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED ONE HUNDRED U.S. DOLLARS (USD $100), REGARDLESS
      OF THE THEORY OF LIABILITY OR THE NUMBER OF CLAIMS ASSERTED.
    </p>
    <p>
      CERTAIN JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES OR LIMITATIONS OF LIABILITY.
      IN SUCH JURISDICTIONS, THE FOREGOING LIMITATIONS SHALL APPLY ONLY TO THE MAXIMUM EXTENT PERMITTED
      BY LAW.
    </p>


    <h2>11. Indemnification</h2>
    <p>
      You agree to defend, indemnify, and hold harmless Vecurate, its affiliates, officers, directors,
      employees, contractors, agents, successors, and assigns from and against any and all claims,
      demands, actions, proceedings, investigations, liabilities, damages, judgments, settlements,
      losses, costs, and expenses (including reasonable attorneys' fees and legal costs) arising out
      of or relating to:
    </p>
    <p>(a) your use of the Service;</p>
    <p>(b) your violation of these Terms;</p>
    <p>(c) your violation of any applicable law, regulation, or third-party right;</p>
    <p>
      (d) any content, reviews, ratings, comments, messages, or other materials that you submit,
      post, publish, transmit, or otherwise make available through the Service;
    </p>
    <p>
      (e) allegations that content you submitted is false, misleading, defamatory, harassing,
      discriminatory, infringing, unlawful, or otherwise harmful; or
    </p>
    <p>(f) your misuse of information obtained through the Service.</p>
    <p>
      Vecurate reserves the right, at its own expense, to assume the exclusive defense and control of
      any matter subject to indemnification by you, and you agree to cooperate fully with Vecurate in
      asserting any available defenses.
    </p>
    <p>
      This indemnification obligation will survive the termination of your account and your use of
      the Service.
    </p>

    <h2>12. Termination</h2>
    <p>
      You may request termination of your account at any time by emailing{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>. Vecurate may suspend or terminate
      your access to all or part of the Service at any time, with or without notice, for any reason,
      including violation of these Terms. Sections that by their nature should survive termination
      (including ownership, disclaimers, limitations of liability, indemnification, and dispute
      resolution) will survive.
    </p>

    <h2>13. Governing Law and Dispute Resolution</h2>
    <p>
      These Terms are governed by the laws of the Commonwealth of Pennsylvania, without regard to its
      conflict-of-laws principles. Except where prohibited by law, any dispute arising out of or
      relating to these Terms or the Service will be resolved exclusively in the state or federal
      courts located in Allegheny County, Pennsylvania, and you consent to the personal jurisdiction
      of those courts.
    </p>

    <h2>14. Changes to These Terms</h2>
    <p>
      We may update these Terms from time to time. If we make material changes, we will notify you by
      updating the "Last updated" date and, where appropriate, by additional means such as an in-app
      notice or email. Your continued use of the Service after the changes take effect constitutes your
      acceptance of the revised Terms.
    </p>

    <h2>15. Contact</h2>
    <p>
      Questions about these Terms can be sent to{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>.
    </p>

    <div className="callout">
      <strong>Note:</strong> This document is provided for informational purposes and should be reviewed
      by qualified legal counsel before being relied upon in any specific jurisdiction.
    </div>
  </LegalPage>
);

export default TermsPage;
